/**
 * @file   src\mobile\components\GitchaMobileFilter.js
 * @brief  This file is responsible for handle filter in mobile view
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import { InputGroup, Button, FormControl } from "react-bootstrap";
import SearchIcon from "../searchIcn";
import SearchCloseIcon from "../searchCloseIcn";
import AdFilterPopup from "../AdFilterPopup";
import Search from "../Home/Search";
import {
  BUY,
  FILTER_QUERY,
  LONG_RENT,
  RECENT_SEARCHES,
  SEARCHED_LOCATION,
  SHORT_RENT,
  USER_LOCATION_KEY,
} from "../../../constants/common";
import {
  getLocalStorage,
  getUserId,
  removeLocalStorage,
  setLocalStorage,
  trimString,
} from "../../../helpers/common";
import {
  checkGitchaAvailable,
  getGeoLocation,
} from "../../../services/commonServices";
import { usePrevious } from "../../../hooks/usePrevious";
import { capitalizeString } from "../../../actions/common";

const GitchaMobileFilter = (props) => {
  const {
    filterQuery,
    updateFilter,
    clearFilterQuery,
    handleFilterApply,
    setSearchView,
    isSearchView,
    showFilterPopup,
    handleFilterPopUpClose,
    handleFilterPopUpShow,
    setUserLocation,
    userLocation,
    gitchaNotAvailble,
    searchedLocation,
    setFilterQueryForApply,
    filterQueryForApply,
    setAdType,
    handleStayRequestPopupOpen,
    handleGitchaNotAvailableLocation,
  } = props;

  const turnOffStay = process.env.REACT_APP_TURN_OFF_STAY ? true : false;

  const adType = _.get(filterQuery, "adType", null);

  const filterQueryFromStorage = getLocalStorage("filterQuery");
  const [location, setLocation] = useState("");
  const [formattedAddress, setFormattedAddress] = useState(
    _.get(searchedLocation, "formatted_address", ""),
  );
  const browserLocation = getLocalStorage("userLocation");
  const [locationList, setLocationList] = useState([]);
  const locationString = getLocalStorage("LocationString");
  const userId = getUserId();
  const [locationSearched, setLocationSearched] = useState({});

  useEffect(() => {
    if (locationString != null && userId) {
      const reqPayload = `address=${capitalizeString(
        locationString,
      )}&components=country:US`;
      getAddresseDetails({ reqPayload: reqPayload });
    } else {
      if (!searchedLocation) {
        clearLocationStates();
      } else {
        setLocationSearched(searchedLocation);
        setLocation(_.get(searchedLocation, "formatted_address", ""));
      }
    }
  }, [searchedLocation, gitchaNotAvailble, locationString, userId]);

  useEffect(() => {
    const searchKey = _.get(getLocalStorage(FILTER_QUERY), "search", "");
    setLocation(searchKey);
  }, []);

  //To check gitcha is available in selected location
  const checkGitchaAvailableInLoc = async (
    city,
    state,
    county,
    formatted_address,
    locationObject,
    filterObject,
    noLocation,
  ) => {
    const reqPayLoad = {
      county: county,
      state: state,
    };
    await checkGitchaAvailable(reqPayLoad).then((data) => {
      if (_.get(data, "result") == true) {
        if (filterObject) {
          updateFilter(filterObject);
        }
        locationObject["isFromAvailableCities"] = true;
        setLocalStorage("searchedLocation", locationObject);
        if (!noLocation) {
          setLocation(
            city != undefined ? city + ", " + state : formatted_address,
          );
        }
        setUserLocation(locationObject);
        handleGitchaNotAvailableLocation(false);
      } else {
        setLocalStorage("searchedLocation", locationObject);
        setUserLocation(locationObject);
        handleGitchaNotAvailableLocation(true);
        setLocation("");
      }
    });
  };

  useEffect(() => {
    if (!isSearchView) {
      setLocation("");
      updateFilter({ search: "" });
    }
  }, [isSearchView]);

  const prevAdType = usePrevious(adType);

  useEffect(() => {
    setFilterQueryForApply(null);
    if (adType === LONG_RENT || adType === SHORT_RENT) {
      updateFilter({
        typeOfPlace:
          _.get(filterQueryFromStorage, "typeOfPlace", []).length > 0
            ? _.get(filterQueryFromStorage, "typeOfPlace", [])
            : [10, 11, 6],
      });
    } else {
      updateFilter({ typeOfPlace: [] });
    }
  }, [adType]);

  /**
   * To handle change adTypes in filter
   */
  const handleAdTypeClick = (selectedType) => {
    if (turnOffStay && selectedType === SHORT_RENT) {
      handleStayRequestPopupOpen();
    } else {
      removeLocalStorage("FilterAdtype");
      removeLocalStorage("filterQuery");
      removeLocalStorage("selectedPropertyId");
      removeLocalStorage("priceRangeMatch");
      setAdType(selectedType);
      setLocalStorage("FilterAdtype", selectedType);
      updateFilter({ adType: selectedType });
    }
  };
  /**
   * To clear addresses listed below search bar
   */
  const clearAddressList = () => {
    setLocationList([]);
  };

  /**
   * To clear location states when selected location is changed
   */
  const clearLocationStates = () => {
    if (!locationString) {
      removeLocalStorage(SEARCHED_LOCATION);
      if (!browserLocation?.isFromAvailableCities) {
        setUserLocation(null);
      } else {
        setUserLocation(browserLocation);
        setFormattedAddress(_.get(browserLocation, "formatted_address", null));
      }
    }
    setLocation("");
  };

  /**
   * To handle locations listed in Recent searches
   */
  const handleResentSelect = (resultSet) => {
    const lat = _.get(resultSet, "lat", "");
    const lng = _.get(resultSet, "lng", "");
    const city = _.get(resultSet, "city", "");
    const state = _.get(resultSet, "state", "");
    const county = _.get(resultSet, "county", "");
    const section = _.get(resultSet, "section", "");
    const selectedText = _.get(resultSet, "key", "");
    setLocation(selectedText);
    if (section === "locSearch" || section === "currentLoc") {
      const filterObject = {
        search: "",
        lat: lat,
        long: lng,
        city: city,
        state: state,
      };
      const locationObject = {
        latitude: lat,
        longitude: lng,
        formatted_address: selectedText,
        city: city,
        state: state,
      };
      checkGitchaAvailableInLoc(
        city,
        state,
        county,
        selectedText,
        locationObject,
        filterObject,
      );
    } else if (section === "keyword") {
      if (locationSearched?.isFromAvailableCities)
        locationSearched["search"] = selectedText;
      setUserLocation(locationSearched);
    }
    clearAddressList();
    setSearchView(false);
  };

  /**
   * To handle location selected from various sections in location search view
   */
  const handleSelection = ({ section, selectedLoc }) => {
    removeLocalStorage(SEARCHED_LOCATION);
    let lat, lng;
    let searchKey;
    let city, state, county;
    if (section === "currentLoc") {
      clearLocationStates();
      const currentLoc = getLocalStorage(USER_LOCATION_KEY);
      if (currentLoc) {
        const currLat = _.get(currentLoc, "latitude", null);
        const currLng = _.get(currentLoc, "longitude", null);
        lat = currLat;
        lng = currLng;
        city = _.get(currentLoc, "city", null);
        state = _.get(currentLoc, "state", null);
        county = _.get(currentLoc, "county", null);
        searchKey = city + ", " + state;
        const filterObject = {
          lat: currLat,
          long: currLng,
          search: "",
          city: city,
          state: state,
        };
        const locationObject = {
          latitude: currLat,
          longitude: currLng,
          formatted_address: searchKey,
          city: city,
          state: state,
        };
        checkGitchaAvailableInLoc(
          city,
          state,
          county,
          searchKey,
          locationObject,
          filterObject,
        );
      }
    } else if (section === "keyword") {
      searchKey = location || selectedLoc;
      if (locationSearched?.isFromAvailableCities)
        locationSearched["search"] = searchKey;
      setUserLocation(locationSearched);
      setLocalStorage(SEARCHED_LOCATION, locationSearched);
    } else {
      clearLocationStates();
      const geometry = _.get(selectedLoc, "geometry", "");
      const locBounds = _.get(geometry, "location", "");
      const formatted_address = _.get(selectedLoc, "formatted_address", "");
      const firstAddressComponent = _.get(
        selectedLoc,
        "address_components",
        "",
      );
      const stateSearched = firstAddressComponent?.filter(function (ca) {
        return ca.types?.[0] === "administrative_area_level_1";
      });
      if (stateSearched?.length) {
        state = _.get(stateSearched?.[0], "short_name", "");
      }
      const locality = firstAddressComponent?.filter(function (ca) {
        return ca?.types?.[0] === "locality";
      });
      const countyList = firstAddressComponent.filter(function (ca) {
        return ca.types?.[0] === "administrative_area_level_2";
      });

      if (countyList.length) {
        county = _.get(countyList?.[0], "short_name", "");
      }
      if (locality?.length) {
        city = _.get(locality?.[0], "long_name", "");
      }
      if (city != undefined) {
        searchKey = city + ", " + state;
        setFormattedAddress(searchKey);
      } else {
        searchKey = formatted_address;
        setFormattedAddress(searchKey);
      }
      lat = _.get(locBounds, "lat", "");
      lng = _.get(locBounds, "lng", "");
      const locationObject = {
        latitude: _.get(locBounds, "lat", ""),
        longitude: _.get(locBounds, "lng", ""),
        formatted_address:
          city != undefined ? city + ", " + state : formatted_address,
        city: city,
        state: state,
      };
      checkGitchaAvailableInLoc(
        city,
        state,
        county,
        formatted_address,
        locationObject,
      );
      clearAddressList();
    }
    if (searchKey) {
      let recentSearches = getLocalStorage(RECENT_SEARCHES) || [];
      const index =
        recentSearches?.length === 0 ? 1 : recentSearches.length + 1;
      searchKey = { key: searchKey, section: section, index: index };
      if (lat) {
        searchKey.lat = lat;
      }
      if (lng) {
        searchKey.lng = lng;
      }
      if (city) {
        searchKey.city = city;
      }
      if (state) {
        searchKey.state = state;
      }
      if (county) {
        searchKey.county = county;
      }
      const filteredArray = recentSearches.filter(
        (item) => item.key === searchKey.key,
      );
      if (filteredArray?.length === 0) {
        if (recentSearches?.length >= 5) {
          recentSearches.shift();
        }
        recentSearches.push(searchKey);
      }
      setLocalStorage(RECENT_SEARCHES, recentSearches);
    }
    setSearchView(false);
  };

  /**
   * To handle text change in search field
   */
  const handleLocationChange = (val) => {
    const value = val;
    if (trimString(value) !== "") {
      const reqPayload = `address=${value}&components=country:US`;
      getAddresseDetails({ reqPayload: reqPayload });
      setLocation(value);
    } else {
      clearAddressList();
      setLocation("");
    }
  };

  /**
   * To list addresses based on the text entered in search field
   */
  const getAddresseDetails = async ({ reqPayload }) => {
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (results?.length > 0 && formatted_address !== "United States") {
          if (locationString) {
            handleSelection({
              section: "locSearch",
              selectedLoc: results[0],
            });
            removeLocalStorage("LocationString");
          } else {
            setLocationList(results);
          }
        }
      }
    });
  };

  /**
   * To handle clear searched location
   */
  const handleCloseClick = () => {
    setLocation("");
    setLocationSearched(userLocation);
    if (!browserLocation?.isFromAvailableCities) {
      setUserLocation(null);
    } else {
      setUserLocation(browserLocation);
    }
    setSearchView(false);
    removeLocalStorage(SEARCHED_LOCATION);
  };

  /**
   * To enable search view
   */
  const handleSearchClick = () => {
    setSearchView(true);
  };

  return (
    <>
      <div className={isSearchView ? "" : "top-three-comp-shadow"}>
        <div className="d-flex align-items-center me-3 ms-3">
          <div className="search_wrap_mobile_top position-relative">
            <InputGroup className="mobile-search-bar">
              <FormControl
                placeholder="Enter an address, city, or ZIP code"
                onClick={() => handleSearchClick()}
                onKeyUp={() => handleSearchClick()}
                onChange={(e) => {
                  handleLocationChange(e.target.value);
                }}
                value={location}
              />
              <Button
                variant="outline-secondary"
                id="button-addon2"
                className="mobile-search-icon"
              >
                {isSearchView ? (
                  <SearchCloseIcon handleCloseClick={handleCloseClick} />
                ) : (
                  <SearchIcon handleSearchClick={handleSearchClick} />
                )}
              </Button>
            </InputGroup>

            {isSearchView && (
              <Search
                setSearchView={setSearchView}
                isSearchView={isSearchView}
                handleSelection={handleSelection}
                location={location}
                locationList={locationList}
                handleResentSelect={handleResentSelect}
              />
            )}
          </div>
          {!isSearchView && (
            <div>
              <Button
                variant=""
                id="button-addon2"
                className="btn search-filter-btn"
                onClick={() => handleFilterPopUpShow()}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.125 6.87502H5.70312C5.84081 7.41278 6.15356 7.88942 6.59207 8.22979C7.03057 8.57017 7.56989 8.75492 8.125 8.75492C8.68011 8.75492 9.21943 8.57017 9.65793 8.22979C10.0964 7.88942 10.4092 7.41278 10.5469 6.87502H16.875C17.0408 6.87502 17.1997 6.80917 17.3169 6.69196C17.4342 6.57475 17.5 6.41578 17.5 6.25002C17.5 6.08426 17.4342 5.92529 17.3169 5.80808C17.1997 5.69087 17.0408 5.62502 16.875 5.62502H10.5469C10.4092 5.08726 10.0964 4.61062 9.65793 4.27024C9.21943 3.92987 8.68011 3.74512 8.125 3.74512C7.56989 3.74512 7.03057 3.92987 6.59207 4.27024C6.15356 4.61062 5.84081 5.08726 5.70312 5.62502H3.125C2.95924 5.62502 2.80027 5.69087 2.68306 5.80808C2.56585 5.92529 2.5 6.08426 2.5 6.25002C2.5 6.41578 2.56585 6.57475 2.68306 6.69196C2.80027 6.80917 2.95924 6.87502 3.125 6.87502ZM8.125 5.00002C8.37223 5.00002 8.6139 5.07333 8.81946 5.21068C9.02502 5.34803 9.18524 5.54326 9.27985 5.77167C9.37446 6.00007 9.39921 6.25141 9.35098 6.49388C9.30275 6.73636 9.1837 6.95909 9.00888 7.1339C8.83407 7.30872 8.61134 7.42777 8.36886 7.476C8.12639 7.52423 7.87505 7.49948 7.64665 7.40487C7.41824 7.31026 7.22301 7.15004 7.08566 6.94448C6.94831 6.73892 6.875 6.49725 6.875 6.25002C6.875 5.9185 7.0067 5.60056 7.24112 5.36614C7.47554 5.13172 7.79348 5.00002 8.125 5.00002ZM16.875 13.125H15.5469C15.4092 12.5873 15.0964 12.1106 14.6579 11.7702C14.2194 11.4299 13.6801 11.2451 13.125 11.2451C12.5699 11.2451 12.0306 11.4299 11.5921 11.7702C11.1536 12.1106 10.8408 12.5873 10.7031 13.125H3.125C2.95924 13.125 2.80027 13.1909 2.68306 13.3081C2.56585 13.4253 2.5 13.5843 2.5 13.75C2.5 13.9158 2.56585 14.0748 2.68306 14.192C2.80027 14.3092 2.95924 14.375 3.125 14.375H10.7031C10.8408 14.9128 11.1536 15.3894 11.5921 15.7298C12.0306 16.0702 12.5699 16.2549 13.125 16.2549C13.6801 16.2549 14.2194 16.0702 14.6579 15.7298C15.0964 15.3894 15.4092 14.9128 15.5469 14.375H16.875C17.0408 14.375 17.1997 14.3092 17.3169 14.192C17.4342 14.0748 17.5 13.9158 17.5 13.75C17.5 13.5843 17.4342 13.4253 17.3169 13.3081C17.1997 13.1909 17.0408 13.125 16.875 13.125ZM13.125 15C12.8778 15 12.6361 14.9267 12.4305 14.7894C12.225 14.652 12.0648 14.4568 11.9701 14.2284C11.8755 14 11.8508 13.7486 11.899 13.5062C11.9472 13.2637 12.0663 13.041 12.2411 12.8661C12.4159 12.6913 12.6387 12.5723 12.8811 12.524C13.1236 12.4758 13.3749 12.5006 13.6034 12.5952C13.8318 12.6898 14.027 12.85 14.1643 13.0556C14.3017 13.2611 14.375 13.5028 14.375 13.75C14.375 14.0815 14.2433 14.3995 14.0089 14.6339C13.7745 14.8683 13.4565 15 13.125 15Z"
                    fill="black"
                  />
                </svg>
              </Button>
            </div>
          )}
        </div>
        {!isSearchView && (
          <>
            <div className="adBtnsWrap d-flex justify-content-between me-3 ms-3">
              <div
                className={
                  adType === BUY
                    ? "btn-select w-50 pr-6 wanted-card-wrapper"
                    : "w-50 pr-6"
                }
              >
                <Button
                  variant="outline-secondary"
                  id="button-addon2"
                  className="d-flex justify-content-center align-items-center w-100 ln-20 wanted-card-mobile"
                  onClick={() => handleAdTypeClick(BUY)}
                >
                  <span className="buyAdIcn"></span>
                  <span className="wanted-card-item ms-1">Wanted to Buy</span>
                </Button>
              </div>
              <div
                className={
                  adType === LONG_RENT
                    ? "btn-select w-50 pl-6 wanted-card-wrapper"
                    : "w-50 pl-6 wanted-card-wrapper"
                }
              >
                <Button
                  variant="outline-secondary"
                  id="button-addon2"
                  className="d-flex justify-content-center align-items-center wanted-rent-btn w-100 ln-20"
                  onClick={() => handleAdTypeClick(LONG_RENT)}
                >
                  <span className="rentAdIcn"></span>
                  <span className="wanted-card-item ms-1">Wanted to Rent</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      {showFilterPopup && (
        <AdFilterPopup
          showFilterPopup={showFilterPopup}
          onHide={handleFilterPopUpClose}
          filterQuery={filterQuery}
          adType={adType}
          updateFilter={updateFilter}
          clearFilterQuery={clearFilterQuery}
          handleFilterApply={handleFilterApply}
          handleFilterPopUpClose={handleFilterPopUpClose}
          handleFilterPopUpShow={handleFilterPopUpShow}
          filterQueryFromStorage={filterQueryFromStorage}
          setFilterQueryForApply={setFilterQueryForApply}
          filterQueryForApply={filterQueryForApply}
          setLocation={setLocation}
        />
      )}
    </>
  );
};

export default GitchaMobileFilter;
